// features/schedule/components/EkipPicker.tsx
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { StaffAvatar } from "@/components/StaffAvatar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import {
  ALL_BRANCHES_VALUE,
  HANOI_BRANCHES_VALUE,
  SAIGON_BRANCHES_VALUE,
} from "@/features/orders/statusMap";
import { type TreeUnit, regionOfBranchIn, useBranchCatalog, useBranchLabel } from "@/lib/branches";
import { certWarnText } from "@/features/certgate/certLabel";
import { CertChip } from "@/features/certgate/components/CertChip";
import { CertReasonDialog } from "@/features/certgate/components/CertReasonDialog";
import { canGrantCertException } from "@/features/certgate/permissions";
import type { CertVerdict } from "@/features/certgate/schemas";

export type EkipStaffOption = {
  id: string;
  name: string;
  /** Mã phòng ban — quyết định thương hiệu của người này khi chia nhóm. */
  department?: string | null | undefined;
  branch?: string | null | undefined;
  /**
   * Branches assigned in Casdoor, as shown by the "Phạm vi chi nhánh" dialog on
   * the staff page. Decision 0020 makes this the boundary; `branch` is only the
   * person's home branch, and the two disagree for anyone assigned elsewhere.
   */
  branchScope?: { branchCodes?: string[] } | null | undefined;
  /** 'inactive' = đã nghỉ hoặc hồ sơ trùng đã gộp. Không được phân việc mới. */
  status?: string | null | undefined;
  /**
   * Phán quyết chứng chỉ chuyên ngành cho ĐÚNG đơn đang gán (QĐ 0101). Chỉ có
   * khi truyền `orderId` và người này thuộc khối photo/makeup; vắng = dòng
   * không kiểm.
   */
  cert?: CertVerdict | null | undefined;
};

/**
 * Thông tin kèm theo lựa chọn: lý do ngoại lệ khi CHẶN, phán quyết đã xử lý để
 * caller khỏi hỏi lại bằng precheck.
 */
export type EkipPickExtra = { certReason?: string; cert?: CertVerdict | null };

type Props = {
  label: string;
  value: { id: string; name: string } | null;
  /** Optional department filter → GET /staff/ekip-options?department=X (không truyền → GET /staff) */
  department?: string | string[];
  /** Order branch. When present, picker shows only ekip in the same city region. */
  branch?: string | null | undefined;
  /**
   * Mã thương hiệu của đơn (orders.brand_code). Chỉ dùng để đẩy nhóm thợ cùng
   * thương hiệu lên đầu danh sách — KHÔNG lọc ai ra (CEO chốt 2a, 02/10/2026).
   */
  brand?: string | null | undefined;
  disabled?: boolean;
  /** Staff ids to hide from the list (e.g. already picked in a multi-select). */
  excludeIds?: string[];
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Đơn đang gán → cửa chọn thợ trả kèm phán quyết chứng chỉ cho từng người. */
  orderId?: string | undefined;
  onChange: (id: string | null, name: string | null, extra?: EkipPickExtra) => void;
};

/**
 * Lọc theo chế độ chứng chỉ (spec §7.1): ở chế độ CHẶN người bị chặn biến khỏi
 * danh sách, trừ khi người có quyền gán ngoại lệ bật "Hiện cả". Người đang
 * được chọn luôn giữ — cùng lý do với luật đã-nghỉ bên dưới.
 */
export function visibleCertOptions<T extends { id: string; cert?: CertVerdict | null | undefined }>(
  options: T[],
  showAll: boolean,
  canException: boolean,
  keepId?: string | null,
): T[] {
  return options.filter((o) => o.id === keepId || !o.cert?.blocked || (showAll && canException));
}

export function EkipPicker({
  label,
  value,
  department,
  branch,
  brand,
  disabled,
  excludeIds,
  placeholder,
  orderId,
  onChange,
}: Props) {
  const departments = Array.isArray(department) ? department : department ? [department] : [];
  const { data: staff } = useQuery<EkipStaffOption[]>({
    // orderId nằm trong khoá: cùng phòng ban nhưng khác đơn là khác phán quyết.
    queryKey: ["staff", { departments, orderId: orderId ?? "" }],
    queryFn: async () => {
      if (departments.length === 0) {
        const { data } = await api.GET("/staff", {});
        return data ?? [];
      }
      // Cửa chọn thợ riêng, KHÔNG phải GET /staff (kiểm toán 05/09/2026,
      // OD-UI-01): GET /staff thu về đúng một dòng (chính mình) cho sale/lễ
      // tân — họ tạo lịch được nhưng ô này rỗng, không phân được thợ. Cửa
      // này trả id/tên/cơ sở/trạng thái cho mọi người đã đăng nhập, kèm phán
      // quyết chứng chỉ khi biết đơn (QĐ 0101).
      const rows = await Promise.all(
        departments.map(async (dept) => {
          const { data } = await api.GET("/staff/ekip-options", {
            params: { query: { department: dept, ...(orderId ? { orderId } : {}) } },
          });
          return data ?? [];
        }),
      );
      const byId = new Map<string, EkipStaffOption>();
      for (const row of rows.flat()) byId.set(row.id, row);
      return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "vi"));
    },
    staleTime: 60_000,
  });

  // Region filtering runs off the catalog tree, the same source the payroll and
  // staff tabs use — a branch in a newly created region must not drop out here.
  // Full catalog: this resolves the region of the order's branch and of each
  // staff member's branch, both already-stored values. A scoped list would send
  // them down the free-text guess path and quietly match the wrong region.
  const { allUnits } = useBranchCatalog();
  const branchLabel = useBranchLabel();
  const canException = canGrantCertException();
  const [showAll, setShowAll] = useState(false);
  const [pendingBlocked, setPendingBlocked] = useState<EkipStaffOption | null>(null);
  const exclude = new Set(excludeIds ?? []);
  // Thợ chụp / trang điểm: chỉ người được gán ĐÚNG chi nhánh của đơn (QĐ 0389).
  // Người còn lại hiện dưới tên chi nhánh của đơn chứ không theo chi nhánh
  // chính trên hồ sơ — người Trường Chinh vừa được gán thêm Nguyễn Khang mà vẫn
  // ghi "Trường Chinh" thì đọc như lọc sai.
  const orderBranchCode = branchCodeOf(branch, allUnits);
  const onSite = (s: EkipStaffOption) => isOnSiteEkipDepartment(s.department);
  const visibleStaff = filterEkipStaffByBranch(staff ?? [], branch, allUnits, {
    exactBranch: onSite,
  }).map((s) => (orderBranchCode && onSite(s) ? { ...s, branch: orderBranchCode } : s));
  // Chi nhánh của đơn chưa ai được gán: nói ra, kẻo ô rỗng đọc như thợ nghỉ hết.
  const branchUnstaffed =
    staff !== undefined &&
    !!orderBranchCode &&
    departments.some(isOnSiteEkipDepartment) &&
    !visibleStaff.some((s) => s.status !== "inactive");
  // Người đã nghỉ / hồ sơ trùng đã gộp không được phân việc mới. Vẫn giữ nếu
  // đang là người được chọn trên đơn: bỏ khỏi danh sách sẽ làm ô chọn rỗng và
  // lần lưu sau sẽ xoá sạch phân công cũ mà không ai chủ ý.
  const options = visibleStaff.filter(
    (s) =>
      (s.status !== "inactive" || s.id === value?.id) && (!exclude.has(s.id) || s.id === value?.id),
  );
  // Chứng chỉ chuyên ngành (QĐ 0101): CHẶN thì giấu người thiếu chứng chỉ —
  // nhưng để lại một dòng nói vì sao danh sách ngắn, kẻo lễ tân tưởng thợ nghỉ.
  const hasBlocked = options.some((o) => o.cert?.blocked);
  const shown = visibleCertOptions(options, showAll, canException, value?.id);
  const emptyLabel = placeholder ?? "— Chưa phân —";
  const groups = groupEkipOptions(shown, {
    orderBranch: branch,
    orderBrand: brand,
    units: allUnits,
    branchLabel,
  });

  const pick = (v: string) => {
    if (v === "_none") {
      onChange(null, null);
      return;
    }
    const s = shown.find((x) => x.id === v);
    if (!s) return;
    if (s.cert?.blocked && s.id !== value?.id) {
      setPendingBlocked(s);
      return;
    }
    if (s.cert?.flagged && !window.confirm(certWarnText(s.name, s.cert))) return;
    onChange(s.id, s.name, { cert: s.cert ?? null });
  };

  const renderItem = (s: EkipStaffOption) => (
    // textValue is required now that the item holds an avatar: Radix
    // otherwise types-ahead on node.textContent, which starts with the
    // fallback initials and would break jump-to-name.
    <SelectItem
      key={s.id}
      value={s.id}
      textValue={[s.name, branchLabel(s.branch)].filter(Boolean).join(" · ")}
    >
      <span className="inline-flex items-center gap-1.5">
        <StaffAvatar staffId={s.id} name={s.name} sizePx={24} />
        {[s.name, branchLabel(s.branch)].filter(Boolean).join(" · ")}
        <CertChip verdict={s.cert} />
      </span>
    </SelectItem>
  );

  return (
    <div className="space-y-1">
      <label className="text-xs text-muted">{label}</label>
      <Select value={value?.id ?? "_none"} onValueChange={pick} disabled={disabled ?? false}>
        <SelectTrigger>
          <SelectValue placeholder={emptyLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">{emptyLabel}</SelectItem>
          {groups.length <= 1
            ? shown.map(renderItem)
            : // Một nhóm = một tiêu đề "Thương hiệu · Chi nhánh"; nhóm khớp đơn
              // đứng đầu (CEO chốt 1a, 02/10/2026). aria-label đặt thẳng để
              // trình đọc màn hình và bài kiểm gọi nhóm đúng tên.
              groups.map((g, i) => (
                <SelectGroup key={g.key} aria-label={g.label}>
                  {i > 0 && <SelectSeparator />}
                  <SelectLabel className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.label}
                  </SelectLabel>
                  {g.items.map(renderItem)}
                </SelectGroup>
              ))}
        </SelectContent>
      </Select>
      {hasBlocked && canException && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            aria-label="Hiện cả người chưa có chứng chỉ"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Hiện cả người chưa có chứng chỉ
        </label>
      )}
      {branchUnstaffed && (
        <p className="text-xs text-muted-foreground">
          Chưa ai được gán chi nhánh {branchLabel(orderBranchCode) || orderBranchCode} — gán ở
          Nhân sự → Phạm vi chi nhánh.
        </p>
      )}
      {hasBlocked && !canException && shown.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Chưa ai có chứng chỉ cho dòng này — hỏi quản lý trực tiếp.
        </p>
      )}
      <CertReasonDialog
        // Đổi người là dựng lại hộp: ô lý do không mang chữ của lần trước.
        key={pendingBlocked?.id ?? "none"}
        open={pendingBlocked !== null}
        personName={pendingBlocked?.name ?? ""}
        label={pendingBlocked?.cert?.label ?? ""}
        onCancel={() => setPendingBlocked(null)}
        onConfirm={(reason) => {
          const s = pendingBlocked;
          setPendingBlocked(null);
          if (s) onChange(s.id, s.name, { certReason: reason, cert: s.cert ?? null });
        }}
      />
    </div>
  );
}

/**
 * Thương hiệu suy từ phòng ban. Chỉ hai phòng chụp có thương hiệu riêng; make
 * up / retouch làm chung nên không gắn — danh sách của họ chỉ chia theo cơ sở.
 */
const DEPARTMENT_BRAND: Record<string, string> = {
  photo_idphoto: "gnat",
  photo_profile: "gnpf",
};

/** Mã thương hiệu suy từ phòng ban ("" khi phòng ban làm chung mọi thương hiệu). */
export function ekipBrandOfDepartment(department: string | null | undefined): string {
  return DEPARTMENT_BRAND[(department ?? "").trim()] ?? "";
}

/** Tên ngắn hiện trên tiêu đề nhóm; thứ tự khoá ở đây là thứ tự xếp mặc định. */
const BRAND_GROUP_LABEL: Record<string, string> = {
  gnat: "Ảnh thẻ",
  gnpf: "Profile",
};

const NO_BRANCH_LABEL = "Chưa ghi chi nhánh";

export type EkipOptionGroup<T> = { key: string; label: string; items: T[] };

/**
 * Chia danh sách thợ thành nhóm "Thương hiệu · Chi nhánh" (CEO chốt 1a · 2a,
 * 02/10/2026 — lễ tân than danh sách trộn Ảnh thẻ với Profile, trộn cả cơ sở).
 *
 *   - Thương hiệu của đơn lên trước, rồi các thương hiệu khác theo thứ tự cố
 *     định; người không thuộc thương hiệu nào (make up, retouch) xuống cuối.
 *     Danh sách chỉ có một thương hiệu thì tiêu đề chỉ ghi chi nhánh.
 *   - Trong mỗi thương hiệu: cơ sở của đơn trước, các cơ sở khác theo tên,
 *     người chưa ghi cơ sở cuối cùng.
 *   - Trong mỗi nhóm giữ nguyên thứ tự đầu vào (đã xếp theo tên).
 *
 * Chỉ SẮP XẾP, không bỏ ai: tổng số người ra luôn bằng số người vào.
 */
export function groupEkipOptions<T extends EkipStaffOption>(
  options: T[],
  ctx: {
    orderBranch?: string | null | undefined;
    orderBrand?: string | null | undefined;
    units?: readonly TreeUnit[];
    branchLabel: (code: string | null | undefined) => string;
  },
): EkipOptionGroup<T>[] {
  const units = ctx.units ?? [];
  const branchKey = (v: string | null | undefined) =>
    branchCodeOf(v, units) || normalizeBranchText(v);
  const brandOf = (o: T) => ekipBrandOfDepartment(o.department);
  const orderBranchKey = branchKey(ctx.orderBranch);
  const orderBrand = (ctx.orderBrand ?? "").trim();
  const multiBrand = new Set(options.map(brandOf)).size > 1;
  const brandOrder = Object.keys(BRAND_GROUP_LABEL);

  const byKey = new Map<string, EkipOptionGroup<T> & { brand: string; branch: string }>();
  for (const o of options) {
    const brand = multiBrand ? brandOf(o) : "";
    const branch = branchKey(o.branch);
    const key = `${brand}|${branch}`;
    let group = byKey.get(key);
    if (!group) {
      const branchText = branch ? ctx.branchLabel(o.branch) || o.branch || branch : NO_BRANCH_LABEL;
      const brandText = brand ? BRAND_GROUP_LABEL[brand] ?? brand : multiBrand ? "Khác" : "";
      group = {
        key,
        brand,
        branch,
        label: [brandText, branchText].filter(Boolean).join(" · "),
        items: [],
      };
      byKey.set(key, group);
    }
    group.items.push(o);
  }

  const brandRank = (b: string) => {
    if (b && b === orderBrand) return -1;
    if (!b) return brandOrder.length + 1;
    const i = brandOrder.indexOf(b);
    return i === -1 ? brandOrder.length : i;
  };
  const branchRank = (b: string) => (b && b === orderBranchKey ? 0 : b ? 1 : 2);

  return Array.from(byKey.values())
    .sort(
      (a, b) =>
        brandRank(a.brand) - brandRank(b.brand) ||
        a.brand.localeCompare(b.brand) ||
        branchRank(a.branch) - branchRank(b.branch) ||
        a.label.localeCompare(b.label, "vi"),
    )
    .map(({ key, label, items }) => ({ key, label, items }));
}

/**
 * Phòng làm việc TẠI cơ sở: thợ chụp và thợ trang điểm. Ô chọn ê-kíp chỉ chào
 * họ cho đơn ở đúng chi nhánh họ được gán (QĐ 0389). Hậu kỳ đứng ngoài — thợ
 * hậu kỳ được mượn chéo cơ sở (QĐ 0343).
 */
const ON_SITE_EKIP_DEPARTMENTS = new Set([
  "photo",
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
  "makeup",
]);

export function isOnSiteEkipDepartment(department: string | null | undefined): boolean {
  return ON_SITE_EKIP_DEPARTMENTS.has((department ?? "").trim());
}

/**
 * Narrows the ekip list to the people who may work the order's branch.
 *
 * Two rules, because two kinds of row exist:
 *
 *   - An assigned scope (`branchScope.branchCodes`) is matched against the
 *     order's region — or, for rows `exactBranch` picks, against the order's
 *     branch itself (QĐ 0389: photo/makeup at Nguyễn Khang must not be offered
 *     someone assigned only to Trường Chinh).
 *   - No assigned scope — Casdoor has not synced the person yet — falls back to
 *     `staff.branch` under the same rule. Dropping them instead would empty
 *     the picker for a sync lag nobody in the studio can see or fix.
 *
 * Both rules fail closed across cities: a person scoped to Saigon is not
 * offered for a Hanoi order.
 */
export function filterEkipStaffByBranch<T extends EkipStaffOption>(
  staff: T[],
  orderBranch?: string | null,
  units: readonly TreeUnit[] = [],
  opts: { exactBranch?: (s: T) => boolean } = {},
): T[] {
  const orderRegion = branchRegion(orderBranch, units);
  if (!orderRegion) return staff;
  const orderCode = branchCodeOf(orderBranch, units);
  const inRegion = (s: EkipStaffOption) =>
    isAllBranch(s.branch) || branchRegion(s.branch, units) === orderRegion;

  // An order whose branch resolves to no catalog entry cannot be matched against
  // assigned codes at all. The whole filter degrades to the region rule rather
  // than hiding everyone who has a scope.
  if (!orderCode) return staff.filter(inRegion);

  return staff.filter((s) => {
    const assigned = assignedBranchCodes(s);
    if (opts.exactBranch?.(s)) {
      const codes = assigned.length > 0 ? assigned : [s.branch ?? ""];
      return codes.some((code) => coversOrderBranch(code, orderCode, orderRegion, units));
    }
    return assigned.length > 0
      ? assigned.some((code) => assignedBranchMatchesOrder(code, orderCode, orderRegion, units))
      : inRegion(s);
  });
}

/**
 * Một mã phạm vi có phủ ĐÚNG cơ sở của đơn không: chính mã đó, "all", hoặc mã
 * miền `<nhóm>_all` của miền chứa đơn. Mã cơ sở khác cùng thành phố KHÔNG phủ.
 * Nhận cả tên gõ tay của hồ sơ cũ ("NGUYỄN KHANG - HÀ NỘI") qua danh mục.
 */
function coversOrderBranch(
  value: string,
  orderCode: string,
  orderRegion: string,
  units: readonly TreeUnit[],
): boolean {
  if (isAllBranch(value)) return true;
  const token = normalizeBranchText(value);
  if (!token) return false;
  if (token.endsWith("_all")) return regionOfBranchIn(token, units) === orderRegion;
  return token === orderCode || branchCodeOf(value, units) === orderCode;
}

/** Assigned branch codes, empty when the person has no synced Casdoor scope. */
function assignedBranchCodes(staff: EkipStaffOption): string[] {
  const codes = staff.branchScope?.branchCodes;
  if (!Array.isArray(codes)) return [];
  return codes.filter((code): code is string => typeof code === "string" && code.trim() !== "");
}

function assignedBranchMatchesOrder(
  assignedCode: string,
  orderCode: string,
  orderRegion: string,
  units: readonly TreeUnit[],
): boolean {
  if (assignedCode === orderCode || isAllBranch(assignedCode)) return true;
  return branchRegion(assignedCode, units) === orderRegion;
}

/**
 * Resolves an order's branch to a catalog code, accepting the hand-typed labels
 * older orders carry ("LÊ VĂN SỸ"). Returns "" when nothing in the catalog
 * matches — the caller must not treat that as "matches nothing".
 */
function branchCodeOf(value: string | null | undefined, units: readonly TreeUnit[]): string {
  const token = normalizeBranchText(value);
  if (!token) return "";
  const matched = units.find(
    (unit) =>
      unit.kind === "branch" &&
      [unit.code, unit.name, unit.shortLabel].some(
        (candidate) => candidate && normalizeBranchText(candidate) === token,
      ),
  );
  return matched?.code ?? "";
}

/**
 * Resolves a branch — code or free-typed label — to its region.
 *
 * The catalog tree is the authority. The text matching below exists only for
 * ekip fields holding a hand-typed branch name that was never a catalog code;
 * without it those rows would drop out of the region filter entirely.
 */
function branchRegion(value: string | null | undefined, units: readonly TreeUnit[]): string {
  const token = normalizeBranchText(value);
  if (!token) return "";
  if (token === HANOI_BRANCHES_VALUE || token === "tat ca chi nhanh ha noi") return "hn";
  if (token === SAIGON_BRANCHES_VALUE || token === "tat ca chi nhanh sai gon") return "sg";

  // Exact code first, then a label match against the catalog.
  const byCode = units.find((unit) => unit.code === token);
  const matched =
    byCode ??
    units.find((unit) =>
      [unit.code, unit.name, unit.shortLabel].some(
        (candidate) => candidate && normalizeBranchText(candidate) === token,
      ),
    );
  if (matched) {
    const region = regionOfBranchIn(matched.code, units);
    if (region !== "all") return region;
  }

  if (token.startsWith("hn") || token.includes("ha noi") || token.includes("nguyen khang")) {
    return "hn";
  }
  if (
    token.startsWith("sg") ||
    token.startsWith("hcm") ||
    token.includes("duong 3/2") ||
    token.includes("le van sy")
  ) {
    return "sg";
  }
  return "";
}

function normalizeBranchText(value?: string | null): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function isAllBranch(value?: string | null): boolean {
  const token = normalizeBranchText(value);
  return token === ALL_BRANCHES_VALUE || token === "tat ca chi nhanh";
}
